import { useState } from "react";
import { Phone, Mail, MapPin, Globe, Send, Sparkles, Clock, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { useToast } from "../hooks/use-toast";
import { schoolInfo } from "../mock/mock";
import { submitContact } from "../services/api";
import ContactInfographic from "../components/infographics/ContactInfographic";

const ContactPage = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitContact(form);
      toast({ title: "Message sent!", description: "We'll get back to you soon." });
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast({ title: "Submission failed", description: err.response?.data?.detail || "Please try again.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const contactCards = [
    { icon: Phone, label: "Call Us", value: schoolInfo.phone, href: `tel:${schoolInfo.phone}`, color: "bg-orange-500" },
    { icon: Mail, label: "Email Us", value: schoolInfo.email, href: `mailto:${schoolInfo.email}`, color: "bg-amber-500" },
    { icon: Globe, label: "Website", value: schoolInfo.website, href: "#", color: "bg-rose-500" },
    { icon: MapPin, label: "Visit Us", value: "Jaipur, Rajasthan", href: "#", color: "bg-teal-500" }
  ];

  return (
    <div>
      <section className="pt-16 pb-12 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
        <div className="container mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white shadow-sm border border-orange-100 mb-4">
            <Sparkles className="w-4 h-4 text-orange-600" />
            <span className="text-xs font-semibold text-orange-700 tracking-widest uppercase">Contact Us</span>
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-stone-800 mb-4">
            Let's <span className="text-orange-600">Connect</span>
          </h1>
          <p className="text-stone-600 max-w-2xl mx-auto">Reach out to us for admissions, campus visits or any questions about our programs.</p>
        </div>
      </section>

      <ContactInfographic />

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-3xl p-8 border border-orange-100">
              <h3 className="font-display text-2xl font-bold text-stone-800 mb-6">Send us a Message</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name" className="text-stone-700 font-semibold">Name</Label>
                  <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your Name" className="mt-1.5 bg-white h-11 border-orange-200" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="email" className="text-stone-700 font-semibold">Email</Label>
                    <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" className="mt-1.5 bg-white h-11 border-orange-200" />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-stone-700 font-semibold">Phone</Label>
                    <Input id="phone" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 XXX" className="mt-1.5 bg-white h-11 border-orange-200" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="message" className="text-stone-700 font-semibold">Message</Label>
                  <Textarea id="message" required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="How can we help?" className="mt-1.5 bg-white border-orange-200 min-h-[120px]" />
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-full h-11 btn-shine">
                  {loading ? <><Loader2 className="mr-2 w-4 h-4 animate-spin" /> Sending...</> : <>Send Message <Send className="ml-2 w-4 h-4" /></>}
                </Button>
              </form>
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-3xl p-8 border border-orange-100 shadow-md">
                <div className="flex items-start gap-4 mb-5">
                  <div className="w-12 h-12 rounded-xl bg-orange-500 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-display font-bold text-lg text-stone-800">Campus Address</p>
                    <p className="text-stone-600 text-sm mt-1">{schoolInfo.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-amber-500 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-display font-bold text-lg text-stone-800">School Hours</p>
                    <p className="text-stone-600 text-sm mt-1">Mon - Sat: 8:00 AM - 5:00 PM</p>
                    <p className="text-stone-600 text-sm">Sunday: Closed</p>
                  </div>
                </div>
              </div>
              <div className="rounded-3xl overflow-hidden shadow-md h-64 border border-orange-100 bg-gradient-to-br from-orange-100 to-amber-100 flex items-center justify-center">
                <div className="text-center p-6">
                  <MapPin className="w-12 h-12 text-orange-600 mx-auto mb-3" />
                  <p className="font-display font-bold text-xl text-stone-800">Jaipur, Rajasthan</p>
                  <p className="text-stone-600 text-sm mt-1">The Pink City of India</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
